import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  ALL_DEPARTMENTS_SCOPE,
  useWorkspaceDepartmentScope,
} from '../../utils/workspaceDepartmentScope';

type WorkspaceScopeBadgeProps = {
  accessToken?: string | null;
  hasSidebar?: boolean;
  isAdmin?: boolean;
};

/** Fixed department scope bar directly under the workspace navbar. */
export function WorkspaceScopeBadge({
  accessToken,
  hasSidebar = false,
  isAdmin = false,
}: WorkspaceScopeBadgeProps) {
  const { t } = useTranslation();
  const barRef = React.useRef<HTMLDivElement | null>(null);
  const {
    departmentScope,
    workspaceDepartments,
    setDepartmentScope,
  } = useWorkspaceDepartmentScope({
    accessToken,
    enabled: Boolean(accessToken),
    isAdmin,
  });

  React.useEffect(() => {
    const el = barRef.current;
    if (!el) {
      document.documentElement.style.setProperty('--workspace-scope-badge-h', '0px');
      return undefined;
    }
    const syncHeight = () => {
      document.documentElement.style.setProperty(
        '--workspace-scope-badge-h',
        `${el.offsetHeight}px`,
      );
    };
    syncHeight();
    const observer = new ResizeObserver(syncHeight);
    observer.observe(el);
    window.addEventListener('resize', syncHeight);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', syncHeight);
      document.documentElement.style.setProperty('--workspace-scope-badge-h', '0px');
    };
  }, [workspaceDepartments.length]);

  if (workspaceDepartments.length === 0) return null;

  return (
    <div
      ref={barRef}
      className={`fixed z-40 flex items-center justify-end gap-2 border-b border-indigo-900/25 bg-indigo-950 px-3 py-1 sm:px-4 ${
        hasSidebar ? 'left-0 right-0 md:left-56' : 'left-0 right-0'
      }`}
      style={{
        top: 'calc(var(--workspace-header-h, 64px) + var(--workspace-setup-banner-h, 0px))',
      }}
    >
      <span className="shrink-0 text-[9px] font-bold uppercase tracking-wider text-indigo-200">
        {t('header.department_scope_label', { defaultValue: 'Scope' })}
      </span>
      <select
        value={departmentScope}
        onChange={(e) => {
          setDepartmentScope(String(e.target.value || ALL_DEPARTMENTS_SCOPE));
        }}
        className="max-w-[min(100%,14rem)] truncate rounded-md border border-indigo-400/50 bg-indigo-800 px-2 py-0.5 text-[11px] font-semibold text-white outline-none focus:border-indigo-300 sm:max-w-xs"
        aria-label={t('header.department_scope', { defaultValue: 'Department scope' })}
      >
        {isAdmin ? (
          <option value={ALL_DEPARTMENTS_SCOPE}>
            {t('header.department_scope_all', { defaultValue: 'All departments' })}
          </option>
        ) : null}
        {workspaceDepartments.map((dep) => (
          <option key={dep.id} value={dep.slug}>
            {dep.name}
          </option>
        ))}
      </select>
    </div>
  );
}

export default WorkspaceScopeBadge;
